import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Send, Loader2, ChevronDown, ChevronUp, X } from 'lucide-react';
import { sendEmail, fetchEmailTemplates } from '@/lib/api';
import type { EmailTemplate } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface EmailComposerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateId?: string;
  candidateEmail?: string;
  jobId?: string;
}

export function EmailComposer({
  open,
  onOpenChange,
  candidateId,
  candidateEmail,
  jobId,
}: EmailComposerProps) {
  const { addToast } = useToast();
  const [toAddress, setToAddress] = useState(candidateEmail ?? '');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('none');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [ccInput, setCcInput] = useState('');
  const [bccInput, setBccInput] = useState('');
  const [ccTags, setCcTags] = useState<string[]>([]);
  const [bccTags, setBccTags] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setToAddress(candidateEmail ?? '');
      setSubject('');
      setBody('');
      setSelectedTemplateId('none');
      setShowCcBcc(false);
      setCcInput('');
      setBccInput('');
      setCcTags([]);
      setBccTags([]);
    }
  }, [open, candidateEmail]);

  const { data: templates = [] } = useQuery({
    queryKey: ['email-templates'],
    queryFn: fetchEmailTemplates,
    enabled: open,
  });

  const sendMutation = useMutation({
    mutationFn: sendEmail,
    onSuccess: () => {
      addToast({
        type: 'success',
        title: 'Email sent',
        message: `Email sent successfully to ${toAddress}`,
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      addToast({
        type: 'error',
        title: 'Failed to send email',
        message: error.message,
      });
    },
  });

  function handleTemplateChange(templateId: string) {
    setSelectedTemplateId(templateId);
    if (templateId === 'none') return;
    const template = templates.find((t: EmailTemplate) => t.id === templateId);
    if (template) {
      setSubject(template.subject);
      setBody(template.body);
    }
  }

  function handleCcKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if ((e.key === 'Enter' || e.key === ',') && ccInput.trim()) {
      e.preventDefault();
      const email = ccInput.trim().replace(/,$/, '');
      if (email && !ccTags.includes(email)) {
        setCcTags([...ccTags, email]);
      }
      setCcInput('');
    }
  }

  function handleBccKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if ((e.key === 'Enter' || e.key === ',') && bccInput.trim()) {
      e.preventDefault();
      const email = bccInput.trim().replace(/,$/, '');
      if (email && !bccTags.includes(email)) {
        setBccTags([...bccTags, email]);
      }
      setBccInput('');
    }
  }

  function removeCc(email: string) {
    setCcTags(ccTags.filter((e) => e !== email));
  }

  function removeBcc(email: string) {
    setBccTags(bccTags.filter((e) => e !== email));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const hasBodyContent = body.replace(/<[^>]*>/g, '').trim().length > 0;
    if (!toAddress.trim() || !subject.trim() || !hasBodyContent) return;

    sendMutation.mutate({
      toAddress: toAddress.trim(),
      subject: subject.trim(),
      body: body,
      candidateId,
      jobId,
      templateId: selectedTemplateId !== 'none' ? selectedTemplateId : undefined,
      cc: ccTags.length > 0 ? ccTags : undefined,
      bcc: bccTags.length > 0 ? bccTags : undefined,
    });
  }

  // RichTextEditor returns '<p></p>' when empty, so strip HTML tags to check for actual content
  const bodyHasContent = body.replace(/<[^>]*>/g, '').trim().length > 0;
  const canSubmit = toAddress.trim() && subject.trim() && bodyHasContent && !sendMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Compose Email</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Template Selector */}
          <div className="space-y-1.5">
            <Label className="text-text-secondary">Template</Label>
            <Select value={selectedTemplateId} onValueChange={handleTemplateChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a template (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No template</SelectItem>
                {templates.map((template: EmailTemplate) => (
                  <SelectItem key={template.id} value={template.id}>
                    <span className="flex items-center gap-2">
                      {template.name}
                      {template.category && (
                        <span className="text-text-tertiary text-xs">({template.category})</span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* To */}
          <div className="space-y-1.5">
            <Label className="text-text-secondary">To</Label>
            <Input
              type="email"
              placeholder="recipient@example.com"
              value={toAddress}
              onChange={(e) => setToAddress(e.target.value)}
              required
            />
          </div>

          {/* CC/BCC Toggle */}
          <button
            type="button"
            onClick={() => setShowCcBcc(!showCcBcc)}
            className="flex items-center gap-1 text-xs text-text-tertiary hover:text-text-secondary transition-colors"
          >
            {showCcBcc ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {showCcBcc ? 'Hide' : 'Show'} CC/BCC
          </button>

          {/* CC */}
          {showCcBcc && (
            <>
              <div className="space-y-1.5">
                <Label className="text-text-secondary">CC</Label>
                <div className="flex flex-wrap gap-1.5 p-2 rounded-md border border-white/20 bg-bg-elevated min-h-[40px] items-center">
                  {ccTags.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/10 text-xs text-text-primary"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => removeCc(email)}
                        className="text-text-tertiary hover:text-text-primary"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyDown={handleCcKeyDown}
                    placeholder={ccTags.length === 0 ? 'Add CC recipients (press Enter)' : ''}
                    className="flex-1 min-w-[120px] bg-transparent text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none"
                  />
                </div>
              </div>

              {/* BCC */}
              <div className="space-y-1.5">
                <Label className="text-text-secondary">BCC</Label>
                <div className="flex flex-wrap gap-1.5 p-2 rounded-md border border-white/20 bg-bg-elevated min-h-[40px] items-center">
                  {bccTags.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/10 text-xs text-text-primary"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => removeBcc(email)}
                        className="text-text-tertiary hover:text-text-primary"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={bccInput}
                    onChange={(e) => setBccInput(e.target.value)}
                    onKeyDown={handleBccKeyDown}
                    placeholder={bccTags.length === 0 ? 'Add BCC recipients (press Enter)' : ''}
                    className="flex-1 min-w-[120px] bg-transparent text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none"
                  />
                </div>
              </div>
            </>
          )}

          {/* Subject */}
          <div className="space-y-1.5">
            <Label className="text-text-secondary">Subject</Label>
            <Input
              placeholder="Email subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
          </div>

          {/* Body */}
          <div className="space-y-1.5">
            <Label className="text-text-secondary">Body</Label>
            <RichTextEditor
              value={body}
              onChange={(html) => setBody(html)}
              placeholder="Write your email message..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {sendMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send Email
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
